import { usePostHog } from 'posthog-js/react/slim';
import { useCallback, useEffect, useRef } from 'react';
import type { Survey } from 'posthog-js';

import { useAddToast, useToasts } from '../../contexts/ToastContext';

import { hasLegacyDismissal, recordSurveyLastSeen } from './surveySeenStorage';

type Params = {
  /** Dispatch toasts only when the onboarding gate has passed AND the user is authenticated. */
  enabled: boolean;
  /** The onboarding survey is excluded here; it renders as a fullscreen takeover instead. */
  onboardingSurveyId: string | undefined;
};

/**
 * Dispatches non-onboarding PostHog surveys (e.g. NPS) as bottom-right toasts.
 *
 * Re-evaluates matching surveys on every `onFeatureFlags` settle rather than
 * once: a survey's auto-created targeting flag is false for the anonymous user
 * and only flips true after `$identify` triggers a flag re-fetch, so a one-shot
 * `getActiveMatchingSurveys` loses that race. `dispatchedSurveyIdsRef` dedupes
 * per mount so re-firing cannot double-show a survey.
 *
 * Surveys dismissed before we started sending `$survey_iteration` are skipped
 * here: those dismissals landed under storage and person-property keys that a
 * recurring survey's targeting flag doesn't read, so PostHog still reports the
 * survey as matching for those users. See `surveySeenStorage`.
 */
export const useSurveyToasts = ({ enabled, onboardingSurveyId }: Params): void => {
  const posthog = usePostHog();
  const addToast = useAddToast();
  const { hideToast } = useToasts();
  const dispatchedSurveyIdsRef = useRef<Set<string>>(new Set());

  const dispatchSurveyToast = useCallback(
    (survey: Survey) => {
      if (dispatchedSurveyIdsRef.current.has(survey.id) || hasLegacyDismissal(survey.id)) {
        return;
      }
      dispatchedSurveyIdsRef.current.add(survey.id);
      // Arms the survey's `seenSurveyWaitPeriodInDays` for the next evaluation.
      recordSurveyLastSeen();
      const toastId = addToast({
        type: 'survey',
        survey,
        onSurveySubmit: () => {
          addToast({
            type: 'success',
            content: 'Thank you for your time answering these questions!',
          });
          hideToast(toastId);
        },
      });
    },
    [addToast, hideToast],
  );

  useEffect(() => {
    if (!enabled || !posthog) {
      return undefined;
    }
    const unsubscribe = posthog.onFeatureFlags(() => {
      posthog.getActiveMatchingSurveys((surveys) => {
        surveys.forEach((survey) => {
          if (survey.id !== onboardingSurveyId) {
            dispatchSurveyToast(survey);
          }
        });
      });
    });
    return unsubscribe;
  }, [enabled, posthog, onboardingSurveyId, dispatchSurveyToast]);
};
